import { useReactiveVar } from '@apollo/client'
import {
  DropResult,
  EditorInput,
  EditorInputState,
  Input,
  InputFontSize,
  InputVariant,
  LinkButton,
  List,
  ListItemQuestionnaireResponseOption,
  TextHighlight,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import { useEffect, useState } from 'react'
import {
  EntryType,
  QuestionSettingCode,
  QuestionValidationError,
  SettingValue
} from '../../../data/model/questionnaire'
import { useDuplicateQuestionnaireEntry } from '../../../hooks/questionnaire/useDuplicateQuestionnaireEntry'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import useQuestionnaireValidation from '../../../hooks/questionnaire/useQuestionnaireValidation'
import useSetQuestionnaireSetting from '../../../hooks/questionnaire/useSetQuestionnaireSetting'
import { useUpdateTextHighlighter } from '../../../hooks/questionnaire/useUpdateTextHighlighter'
import BasicQuestionLayout from '../../../layouts/BasicQuestionLayout'
import { WithEntryOfType, newEntryId } from '../../../utils/questionnaireUtils'
import { getRawValueFromJsonString } from '../CardTitle/CardTitle.utils'
import DefaultOptions from '../QuestionCardDefaultOptions/DefaultOptions'
import QuestionCardSwitches from '../QuestionCardSwitches'
import { flattenEntries } from '../Questionnaire.utils'
import { SurveyQuestionCardHeaderContainer } from '../SurveyQuestionCardHeader'
import TextHighlighterColourSelect, {
  colours
} from './TextHighlighterColourSelect'
import useStyles from './TextHighlighterQuestionCard.styles'

interface Props extends WithEntryOfType<EntryType.TextHighlighterEntryType> {
  isNewlyCreated?: boolean
}

const TextHighlighterQuestionCard = ({ entry, isNewlyCreated }: Props) => {
  const { classes } = useStyles()
  // Using local state to handle real-time updates while debouncing the API call
  const [textHighlighterState, setTextHighlighterState] = useState({
    questionText: entry.entryItem.question?.text,
    questionTextStyling: entry.entryItem.question?.textStyling,
    contentText: entry.entryItem.content.text,
    categories: entry.entryItem.highlighterCategories.map((category) => ({
      colour: category.colour,
      position: category.position,
      responseOptionValue: category.responseOption.value,
      responseOptionId: category.responseOption.responseOptionId
    }))
  })

  const {
    updateTextHighlighterDebounced,
    updateTextHighlighter,
    cancelPendingUpdate
  } = useUpdateTextHighlighter({
    questionId: entry.entryItem.question?.questionId ?? entry.id
  })
  const { duplicateQuestionnaireEntry } = useDuplicateQuestionnaireEntry(
    entry.id
  )
  const { getQuestionValidationErrors } = useQuestionnaireValidation()
  const { handleSettingChange } = useSetQuestionnaireSetting(entry)

  const validationErrors = getQuestionValidationErrors(entry.id)

  const { draftQuestionnaireEntries } = useGetDraftQuestionnaire()
  const flatDraftQuestionnaireEntries = flattenEntries(
    draftQuestionnaireEntries
  )

  const updateQuestion = (inputState: EditorInputState) => {
    const question = inputState.toRaw()
    const newTextStyling = JSON.stringify(question)
    if (newTextStyling !== entry.entryItem.question?.textStyling) {
      setTextHighlighterState((prev) => {
        const newState = {
          ...prev,
          questionText: inputState.toText(),
          questionTextStyling: newTextStyling
        }

        updateTextHighlighterDebounced(newState)

        return newState
      })
    }
  }

  const updateContent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextHighlighterState((prev) => {
      const newState = {
        ...prev,
        contentText: e.target.value
      }

      updateTextHighlighterDebounced(newState)

      return newState
    })
  }

  const updateCategoryColour = ({
    id,
    hex,
    label
  }: {
    id: string
    hex: string
    label: string
  }) => {
    cancelPendingUpdate()
    updateTextHighlighter({
      ...textHighlighterState,
      categories: textHighlighterState.categories.map((category) => {
        return {
          ...category,
          colour:
            category.responseOptionId === id ? { hex, label } : category.colour
        }
      })
    })
  }

  const duplicateQuestion = async () => {
    await duplicateQuestionnaireEntry({
      position: entry.position + 1
    })
  }

  const updateCategoryLabel = ({
    id,
    value
  }: {
    id: string
    value: string
  }) => {
    setTextHighlighterState((prev) => ({
      ...prev,
      categories: prev.categories.map((category) => {
        return {
          ...category,
          responseOptionValue:
            category.responseOptionId === id
              ? value
              : category.responseOptionValue
        }
      })
    }))

    updateTextHighlighterDebounced({
      ...textHighlighterState,
      categories: textHighlighterState.categories.map((category) => {
        return {
          ...category,
          responseOptionValue:
            category.responseOptionId === id
              ? value
              : category.responseOptionValue
        }
      })
    })
  }

  const addCategory = ({ position }: { position: number }) => {
    cancelPendingUpdate()

    const newCategory = {
      colour: { hex: colours[0].value, label: colours[0].name },
      responseOptionValue: ''
    }

    const itemsBefore = textHighlighterState.categories.slice(0, position)
    const itemsAfter = textHighlighterState.categories
      .slice(position)
      .map((category) => ({
        ...category,
        position: category.position + 1
      }))

    updateTextHighlighter({
      ...textHighlighterState,
      categories: [...itemsBefore, { ...newCategory, position }, ...itemsAfter]
    })
  }

  const deleteCategory = (id: string) => {
    cancelPendingUpdate()
    updateTextHighlighter({
      ...textHighlighterState,
      categories: textHighlighterState.categories
        .filter((category) => category.responseOptionId !== id)
        .map((category, index) => ({
          ...category,
          position: index
        }))
    })
  }

  const onQuestionTypeChange = (settingValue: SettingValue) => {
    handleSettingChange(
      [
        {
          settingCode: QuestionSettingCode.BasicChoice,
          settingValue
        }
      ],
      'no-cache'
    )
  }

  const onCategoryReorder = ({ source, destination }: DropResult) => {
    if (
      !destination ||
      destination.droppableId !== 'drop-text-highlighter-categories'
    )
      return
    if (source.index === destination.index) return

    cancelPendingUpdate()
    setTextHighlighterState((prev) => {
      const newCategories = [...prev.categories]
      // Remove the dragged item from its original position
      const [moved] = newCategories.splice(source.index, 1)
      // Insert it at the new position
      newCategories.splice(destination.index, 0, moved)

      const newState = {
        ...prev,
        categories: newCategories.map((category, index) => ({
          ...category,
          position: index
        }))
      }

      updateTextHighlighter(newState)
      return newState
    })
  }

  useEffect(() => {
    setTextHighlighterState({
      questionText: entry.entryItem.question?.text,
      questionTextStyling: entry.entryItem.question?.textStyling,
      contentText: entry.entryItem.content.text,
      categories: entry.entryItem.highlighterCategories.map((category) => ({
        colour: category.colour,
        position: category.position,
        responseOptionValue: category.responseOption.value,
        responseOptionId: category.responseOption.responseOptionId
      }))
    })
  }, [entry])

  const newlyCreatedEntryId = useReactiveVar(newEntryId)
  const isNewlyCreatedEntry =
    !!newlyCreatedEntryId && newlyCreatedEntryId === entry.id

  return (
    <BasicQuestionLayout
      entryId={entry.id}
      questionCardHeader={
        <SurveyQuestionCardHeaderContainer
          entry={entry}
          titleContent={
            <div className={classes.titleContent}>
              <EditorInput
                placeholder="Type your question"
                rawValue={getRawValueFromJsonString(
                  textHighlighterState.questionTextStyling ?? '',
                  textHighlighterState.questionText ?? ''
                )}
                onChange={updateQuestion}
                isError={
                  !isNewlyCreatedEntry &&
                  validationErrors?.errors?.includes(
                    QuestionValidationError.EmptyQuestionTextError
                  )
                }
                helperText="Please add a question title"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={isNewlyCreated}
              />
              <div>
                <label className={classes.label} htmlFor="content">
                  Content to be highlighted
                </label>
                <Input
                  id="content"
                  variant={InputVariant.Outlined}
                  inputFontSize={InputFontSize.m}
                  fullWidth
                  placeholder="Add your content"
                  value={textHighlighterState.contentText}
                  rows={5}
                  isError={
                    !isNewlyCreatedEntry &&
                    validationErrors?.errors?.includes(
                      QuestionValidationError.EmptyContentError
                    )
                  }
                  helperText="Please add some content"
                  onChange={updateContent}
                  multiline
                />
              </div>
            </div>
          }
          onClickDuplicateIcon={duplicateQuestion}
          entries={flatDraftQuestionnaireEntries}
          onQuestionTypeChange={onQuestionTypeChange}
          isMultipleChoice={
            entry.entryItem.settingValues.find(
              (setting) => setting.code === QuestionSettingCode.BasicChoice
            )?.value === SettingValue.MultipleChoice
          }
        />
      }
      switches={
        <QuestionCardSwitches
          entry={entry}
          randomiseQuestionSettingCode={QuestionSettingCode.RandomiseOptions}
        />
      }
      responseOptions={
        <>
          <p className={classes.label}>Highlight categories</p>
          <List
            sx={{ marginBottom: '8px' }}
            canReorderItems
            droppableId="drop-text-highlighter-categories"
            onReorderedItems={onCategoryReorder}
          >
            {textHighlighterState.categories.map((category) => (
              <ListItemQuestionnaireResponseOption
                key={category.responseOptionId}
                responseOptionId={category.responseOptionId}
                placeholder="Type an option"
                value={category.responseOptionValue}
                cannotDelete={textHighlighterState.categories.length <= 2}
                onDelete={deleteCategory}
                onChange={(value) =>
                  updateCategoryLabel({
                    id: category.responseOptionId,
                    value
                  })
                }
                helperText="Please add a category label"
                isError={
                  !isNewlyCreatedEntry &&
                  !!validationErrors?.responseOptionErrors?.find(
                    (error) =>
                      error.responseOptionLk === category.responseOptionId
                  )
                }
                leftElement={
                  <TextHighlighterColourSelect
                    style={{ height: '100%', marginRight: '8px' }}
                    optionValue={category.colour.hex}
                    onChange={({ value }) =>
                      updateCategoryColour({
                        id: category.responseOptionId,
                        hex: value,
                        label:
                          colours.find((c) => c.value === value)?.name ?? ''
                      })
                    }
                  />
                }
                editable
                disableGutters
                onEnter={() => addCategory({ position: category.position + 1 })}
                draggableId={`dr${category.responseOptionId}`}
                index={category.position}
                canBeReordered
              />
            ))}
          </List>
          <LinkButton
            highlight={TextHighlight.Emphasis}
            size={TextSize.m}
            weight={TextWeight.SemiBold}
            onClick={() =>
              addCategory({ position: textHighlighterState.categories.length })
            }
            noPadding
          >
            Add category
          </LinkButton>
        </>
      }
      defaultOptions={
        <DefaultOptions
          entry={entry}
          options={[
            {
              code: QuestionSettingCode.TextHighlighterComments,
              label: 'Enable comments on highlights'
            }
          ]}
        />
      }
    />
  )
}

export default TextHighlighterQuestionCard
